<template>
  <div class="work-relativity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$W = F \cdot s \cdot \cos\theta$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 物理场景渲染 -->
      <div class="simulation-stage dark-stage">
        <!-- 移动的小车/平台 (模拟牵连运动) -->
        <div class="moving-platform" :style="platformStyle">
           <!-- 平台上的物体/人 (模拟相对运动) -->
           <div class="walking-object" :style="objectStyle">
              <div class="force-arrow" v-if="isRunning">
                 <div class="arrow-head"></div>
                 <span class="f-label">F</span>
              </div>
              <div class="mass-block">m</div>
           </div>
           <div class="platform-label">移动平台 (P) v = 50 m/s</div>
        </div>

        <!-- 观测者 A: 地面 -->
        <div class="ground-observer">
           <div class="eye-icon">👁️</div>
           <span>地面观测者 A (地面参考系)</span>
        </div>

        <!-- 观测者 B: 平台 (已跟随) -->
        <div class="platform-observer" v-if="refFrame === 'platform'">
           <div class="eye-icon">🔍</div>
           <span>平台观测者 B (平台参考系)</span>
        </div>

        <div class="track-line"></div>

        <div class="hud-dashboard">
           <div class="hud-group">
              <label>观测位移 s</label>
              <span class="val">{{ currentS.toFixed(1) }} <small>m</small></span>
           </div>
           <div class="hud-group highlight">
              <label>在该参考系下做功 W</label>
              <span class="val">{{ (10 * currentS).toFixed(1) }} <small>J</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
         <div class="control-card">
            <div class="row-flex">
               <div class="label">切换当前观测坐标系：</div>
               <el-radio-group v-model="refFrame" size="small" @change="reset">
                  <el-radio-button label="ground">地面参考系 (A)</el-radio-button>
                  <el-radio-button label="platform">平台参考系 (B)</el-radio-button>
               </el-radio-group>
            </div>
         </div>

         <div class="action-row">
            <el-button type="primary" size="small" @click="start" :disabled="isRunning">开始推行并测功</el-button>
            <el-button size="small" @click="reset">重置实验</el-button>
         </div>

          <div class="discovery-card-compact" v-latex>
             <i class="el-icon-warning-outline"></i>
             <p v-if="refFrame === 'ground'"><strong>结论：</strong>在地面看来，物体的总位移是“平台前进+物体慢走”。位移大，所以**该力做的功也大**。</p>
             <p v-else><strong>结论：</strong>随平台运动时，你觉得它只走了几步。位移小，所以**该力做的功也小**。**功的大小具有相对性**。</p>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkRelativityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
       refFrame: 'ground',
       isRunning: false,
       time: 0,
       vPlatform: 50,
       vRelative: 10,
       timer: null
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '功的相对性：位移因参考系而变';
    },
    currentS() {
      if (this.refFrame === 'ground') {
         return (this.vPlatform + this.vRelative) * this.time;
      } else {
         return this.vRelative * this.time;
      }
    },
    platformStyle() {
      // 地面投影在背景上，车动；车视角，车在中心
      if (this.refFrame === 'ground') {
         const x = this.vPlatform * this.time * 5; // Scaling for visualization
         return { transform: `translateX(${x % 600}px)` };
      } else {
         return { left: '20%' };
      }
    },
    objectStyle() {
      const x = this.vRelative * this.time * 10;
      return { transform: `translateX(${x}px)` };
    }
  },
  methods: {
    start() {
      this.isRunning = true;
      this.animate();
    },
    reset() {
      this.isRunning = false;
      this.time = 0;
      cancelAnimationFrame(this.timer);
    },
    animate() {
       if (!this.isRunning) return;
       this.time += 0.016;
       if (this.time > 3) {
          this.isRunning = false;
       } else {
          this.timer = requestAnimationFrame(this.animate);
       }
    }
  }
}
</script>

<style scoped lang="scss">
.work-relativity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 0.85rem; color: #1e293b; font-weight: 500; } .formula-chip { background: #fee2e2; color: #dc2626; padding: 4px 12px; border-radius: 20px; font-weight: 700; font-family: monospace; font-size: 0.75rem; } }
.simulation-stage { flex: 1; background: #020617; border-radius: 12px; position: relative; overflow: hidden; border: 1.5px solid #1e293b; }
.moving-platform { position: absolute; bottom: 80px; width: 400px; height: 15px; background: #334155; border-radius: 4px; border-top: 2px solid #475569;
  .platform-label { position: absolute; bottom: -25px; left: 50%; transform: translateX(-50%); color: #64748b; font-size: 10px; }
}
.walking-object { position: absolute; bottom: 15px; width: 40px; height: 30px; 
  .mass-block { width: 100%; height: 100%; background: #0ea5e9; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; font-weight: 900; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
  .force-arrow { position: absolute; left: 100%; top: 50%; width: 40px; height: 3px; background: #facc15; transform: translateY(-50%); box-shadow: 0 0 10px #facc15;
     .arrow-head { position: absolute; right: 0; top: -4px; width: 0; height: 0; border-top: 5px solid transparent; border-bottom: 5px solid transparent; border-left: 10px solid #facc15; }
     .f-label { position: absolute; top: -18px; right: 10px; color: #facc15; font-size: 10px; font-weight: bold; }
  }
}
.track-line { position: absolute; bottom: 80px; left: 0; width: 100%; height: 2px; background: #1e293b; z-index: -1; }
.ground-observer { position: absolute; bottom: 20px; left: 50px; color: #94a3b8; font-size: 11px; display: flex; align-items: center; gap: 6px; }
.platform-observer { position: absolute; top: 20px; left: 20%; color: #38bdf8; font-size: 11px; display: flex; align-items: center; gap: 6px; z-index: 10; }
.hud-dashboard { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 8px; 
  .hud-group { background: rgba(15, 23, 42, 0.85); border: 1px solid #1e293b; padding: 6px 15px; border-radius: 8px; text-align: right; 
     label { display: block; font-size: 10px; color: #64748b; } .val { font-size: 1rem; color: #fff; font-weight: 900; font-family: monospace; }
     &.highlight { border-left: 3px solid #10b981; .val { color: #10b981; } }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #fff; padding: 12px; border-radius: 12px; border: 1px solid #e2e8f0; .row-flex { display: flex; align-items: center; justify-content: center; gap: 10px; .label { font-size: 11px; color: #64748b; font-weight: bold; } } }
.action-row { display: flex; gap: 10px; justify-content: center; }
.discovery-card-compact { background: #fdf2f2; padding: 10px 15px; border-radius: 12px; display: flex; align-items: center; gap: 10px; border: 1px solid #fee2e2; i { color: #dc2626; font-size: 1.2rem; } p { margin: 0; font-size: 11px; color: #991b1b; line-height: 1.5; strong { color: #7f1d1d; } } }
</style>
